const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface FocusTrapOptions {
  /** Called when the user presses Escape. Omit to ignore Escape. */
  onEscape?: () => void;
  /** When true, Escape is never handled (e.g. mandatory legal acceptance). */
  preventEscape?: boolean;
  /** Element to focus when the trap activates. Defaults to first focusable child. */
  initialFocus?: HTMLElement | null;
}

function getFocusableElements(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.offsetParent !== null,
  );
}

/**
 * Trap keyboard focus inside a modal/dialog and optionally handle Escape.
 * Restores focus to the previously focused element on destroy.
 */
export function focusTrap(node: HTMLElement, options: FocusTrapOptions = {}) {
  let opts = options;
  const previouslyFocused =
    document.activeElement instanceof HTMLElement ? document.activeElement : null;

  function focusInitial() {
    const target =
      opts.initialFocus ??
      node.querySelector<HTMLElement>('[data-autofocus]') ??
      getFocusableElements(node).find((el) => !el.hasAttribute('data-focus-trap-skip')) ??
      getFocusableElements(node)[0];
    target?.focus();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && opts.onEscape && !opts.preventEscape) {
      event.preventDefault();
      event.stopPropagation();
      opts.onEscape();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements(node);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  // Capture Escape even if focus briefly sits outside the dialog root.
  function handleDocumentKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && opts.onEscape && !opts.preventEscape) {
      event.preventDefault();
      event.stopPropagation();
      opts.onEscape();
    }
  }

  requestAnimationFrame(() => {
    focusInitial();
    // Retry once in case bind:this / children mount a tick later.
    requestAnimationFrame(focusInitial);
  });
  node.addEventListener('keydown', handleKeydown);
  document.addEventListener('keydown', handleDocumentKeydown, true);

  return {
    update(newOptions: FocusTrapOptions) {
      opts = newOptions;
      requestAnimationFrame(focusInitial);
    },
    destroy() {
      node.removeEventListener('keydown', handleKeydown);
      document.removeEventListener('keydown', handleDocumentKeydown, true);
      previouslyFocused?.focus();
    },
  };
}
